'use client'

import type { SortingState } from '@tanstack/react-table'
import * as React from 'react'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Download, Search } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NumberFlow,
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { DateRangeValue } from '../../date-range-select'
import { TrendFilterHeader } from '../trend-filter-header'
import { ConsistencyChart, ReadingActivityChart } from './charts'
import { formatStaminaPerSession, useColumns } from './columns'
import { Empty } from './empty'

const coreRowModel = getCoreRowModel()
const filteredRowModel = getFilteredRowModel()
const sortedRowModel = getSortedRowModel()
const getRowId = (row: { id: string }) => row.id
const EMPTY_FILTERS: { id: string; value: string }[] = []

interface ReadingTrendTableProps {
  type: 'web' | 'school'
  data: RouterOutputs['metrics']['getReadingTrend']
  className?: string
  renderProfileLink: RenderLink
  withSearch?: boolean
  withDownloadButton?: boolean
  renderCreateLink?: RenderLink
  /** Active Aktivitas sub-view + setter — drives the title + Membaca/Belajar pills in the shared header. */
  activityView?: 'lesson' | 'reading'
  onActivityViewChange?: (view: 'lesson' | 'reading') => void
  /** School-class filter value (`type === 'school'` only). `null` = all classes. Owned by the parent. */
  schoolClassId?: string | null
  /** Called when the user picks a class. When provided alongside `type === 'school'`, the class select is shown. */
  onSchoolClassChange?: (value: string | null) => void
  /** Resolved date range driving the trend. Owned by the parent so the query refetches on change. */
  dateRange?: DateRangeValue
  /** Called when the user changes the range. When provided, the date-range select is shown. */
  onDateRangeChange?: (range: DateRangeValue) => void
  /** Member is not assigned to any class — render a distinct empty state with no create CTA. */
  noClassAssigned?: boolean
  /** First name of the signed-in user, used to personalise the no-students empty state. */
  userName?: string
}

export function ReadingTrendTable({
  type,
  data,
  className,
  renderProfileLink,
  withSearch,
  withDownloadButton,
  renderCreateLink,
  activityView,
  onActivityViewChange,
  schoolClassId,
  onSchoolClassChange,
  dateRange,
  onDateRangeChange,
  noClassAssigned,
  userName,
}: ReadingTrendTableProps) {
  const columns = useColumns({ type, renderLink: renderProfileLink, periodDays: data.periodDays })
  const [nameFilter, setNameFilter] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([])

  const metrics = React.useMemo(() => {
    const n = data.trends.length
    if (n === 0) return null
    const totalDuration = data.trends.reduce((s, t) => s + t.totalReadingSeconds, 0)
    const totalSessions = data.trends.reduce((s, t) => s + t.sessionCount, 0)
    const totalBooks = data.trends.reduce((s, t) => s + t.totalBooksRead, 0)
    const totalCompleted = data.trends.reduce((s, t) => s + t.completedBooksCount, 0)
    const avgStaminaMinutes = totalSessions > 0 ? totalDuration / 60 / totalSessions : 0
    const avgSessionsPerChild = totalSessions / n
    const completionRate = totalBooks > 0 ? (totalCompleted / totalBooks) * 100 : 0
    const avgBooksPerChild = totalBooks / n
    return {
      avgStaminaMinutes,
      avgSessionsPerChild,
      completionRate,
      avgBooksPerChild,
    }
  }, [data.trends])

  const downloadCSV = () => {
    const rows = table.getFilteredRowModel().rows

    if (rows.length === 0) {
      toast.error('Tidak ada data untuk diunduh.')
      return
    }

    const headers = ['Murid', 'Konsistensi', 'Stamina', 'Selesai', 'Sesi', 'Buku dibuka']
    const csvRows = rows.map((row) => {
      const o = row.original
      const consistencyPct =
        o.activeDays > 0 && data.periodDays > 0
          ? `${((o.activeDays / data.periodDays) * 100).toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
          : '-'
      const stamina =
        o.sessionCount > 0 && o.averageDurationSeconds > 0
          ? formatStaminaPerSession(o.averageDurationSeconds, o.sessionCount)
          : '-'
      const completionPct =
        o.totalBooksRead > 0
          ? `${((o.completedBooksCount / o.totalBooksRead) * 100).toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
          : '-'
      return [
        `"${o.name}"`,
        `"${consistencyPct}"`,
        `"${stamina}"`,
        `"${completionPct}"`,
        o.sessionCount > 0 ? o.sessionCount : '-',
        o.totalBooksRead > 0 ? o.totalBooksRead : '-',
      ].join(',')
    })

    const csv = [headers.join(','), ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'tren-membaca.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const columnFilters = React.useMemo(
    () => (withSearch ? [{ id: 'id', value: nameFilter }] : EMPTY_FILTERS),
    [withSearch, nameFilter],
  )

  const table = useReactTable({
    data: data.trends,
    columns,
    getCoreRowModel: coreRowModel,
    getFilteredRowModel: filteredRowModel,
    getSortedRowModel: sortedRowModel,
    getRowId,
    state: { columnFilters, sorting },
    onSortingChange: setSorting,
  })

  const hasTrends = data.trends.length > 0

  // Member with no assigned class — no classes to filter, so skip the header and show a bare empty state.
  if (noClassAssigned) {
    return (
      <div className={cn('srd:w-full srd:max-w-[1200px] srd:mx-auto srd:px-4', className)}>
        <Empty type={type} noClass />
      </div>
    )
  }

  return (
    <div className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-8', className)}>
      <div className='srd:w-full srd:border-b srd:border-outline-variant/50 srd:pb-8'>
        <TrendFilterHeader
          type={type}
          activityView={activityView}
          onActivityViewChange={onActivityViewChange}
          schoolClassId={schoolClassId}
          onSchoolClassChange={onSchoolClassChange}
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </div>

      <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:max-w-[1200px] srd:mx-auto srd:px-4'>
        {data.lastCalculatedAt && (
          <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:block srd:text-grey-100')}>
            Terakhir diperbarui{' '}
            {new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(
              new Date(data.lastCalculatedAt),
            )}
          </span>
        )}
        {!hasTrends ? (
          <Empty
            type={type}
            renderLink={renderCreateLink}
            noStudents={!data.hasAnyChildren}
            userName={userName}
            teamId={schoolClassId}
          />
        ) : (
          <>
            <div className='srd:grid srd:grid-cols-1 srd:items-stretch srd:lg:grid-cols-2 srd:gap-3'>
              <section className='srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'>
                <p className={cn(typographyVariants({ variant: 'title-md' }))}>Aktivitas membaca</p>
                <ReadingActivityChart
                  data={data.activityByDay}
                  totalChildren={data.trends.length}
                  from={dateRange?.from}
                  to={dateRange?.to}
                />
              </section>
              <section className='srd:flex srd:flex-col srd:gap-2 srd:rounded-lg srd:border srd:border-grey-30 srd:bg-neutral-white srd:p-3'>
                <p className={cn(typographyVariants({ variant: 'title-md' }))}>Konsistensi membaca</p>
                <ConsistencyChart trends={data.trends} periodDays={data.periodDays} />
              </section>
            </div>

            {metrics && (
              <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4'>
                <section className='srd:flex srd:min-w-0 srd:flex-col srd:gap-2 srd:rounded-2xl srd:bg-neutral-white srd:p-4 srd:text-neutral-black'>
                  <div className='srd:flex srd:flex-col'>
                    <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>
                      Rata-rata stamina baca
                    </p>
                    <NumberFlow
                      value={metrics.avgStaminaMinutes}
                      suffix=' mnt/sesi'
                      format={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }}
                      className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')}
                    />
                  </div>
                  <NumberFlow
                    value={metrics.avgSessionsPerChild}
                    suffix=' sesi / anak'
                    format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                    className={cn(typographyVariants({ variant: 'body-sm' }))}
                  />
                </section>
                <section className='srd:flex srd:min-w-0 srd:flex-col srd:gap-2 srd:rounded-2xl srd:bg-neutral-white srd:p-4 srd:text-neutral-black'>
                  <div className='srd:flex srd:flex-col'>
                    <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-grey-100')}>
                      Rata-rata selesai baca
                    </p>
                    <NumberFlow
                      value={metrics.completionRate}
                      suffix='%'
                      format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                      className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')}
                    />
                  </div>
                  <NumberFlow
                    value={metrics.avgBooksPerChild}
                    suffix=' buku / anak'
                    format={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
                    className={cn(typographyVariants({ variant: 'body-sm' }))}
                  />
                </section>
              </div>
            )}

            {(withSearch === true || withDownloadButton === true) && (
              <div className='srd:flex srd:items-center srd:justify-between srd:gap-2 srd:flex-wrap'>
                {withSearch && (
                  <InputGroup size='sm' className='srd:max-w-xs'>
                    <InputGroupAddon align='inline-start'>
                      <Search className='srd:text-blue-100 srd:size-4' />
                    </InputGroupAddon>
                    <InputGroupInput
                      placeholder='Cari nama...'
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                    />
                  </InputGroup>
                )}
                {withDownloadButton && (
                  <Button variant='ghost' className='srd:ml-auto srd:text-neutral-black' onClick={downloadCSV}>
                    <Download className='srd:size-4 srd:mr-2' />
                    Unduh
                  </Button>
                )}
              </div>
            )}
            <div className='srd:w-full srd:overflow-hidden srd:rounded-md srd:border'>
              <TableComponent>
                <TableHeader className='srd:bg-grey-30'>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} className='srd:hover:bg-transparent'>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id} className='srd:text-nowrap'>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody className='srd:bg-neutral-white'>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className='srd:hover:bg-transparent'>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </TableComponent>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
